import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Progress,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "../../data/mockRABills";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ViewRABillsApproved: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [approvedRABills, setApprovedRABills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    paymentStatus: "",
    contractor: "",
    raBillNumber: "",
    woNumber: "",
    dateRange: null as any,
    site: "",
    project: "",
  });

  useEffect(() => {
    loadApprovedRABills();
  }, []);

  const loadApprovedRABills = () => {
    setLoading(true);
    try {
      // Filter RA Bills that are approved or higher (Posted, Paid)
      const approvedBills = mockRABills.filter((bill) =>
        ["Approved", "Posted", "Paid"].includes(bill.status)
      );
      setApprovedRABills(approvedBills);
    } catch (error) {
      console.error("Error loading approved RA Bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Paid: "success",
      "Partially Paid": "warning",
      Unpaid: "error",
    };
    return statusColors[status] || "default";
  };

  const getPaymentProgress = (
    paidAmount: number,
    totalAmount: number
  ): number => {
    if (totalAmount === 0) return 0;
    return Math.round((paidAmount / totalAmount) * 100);
  };

  const handleViewRABill = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=view`
    );
  };

  const resetFilters = () => {
    setFilters({
      paymentStatus: "",
      contractor: "",
      raBillNumber: "",
      woNumber: "",
      dateRange: null,
      site: "",
      project: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalApproved = approvedRABills.length;
  const totalValue = approvedRABills.reduce(
    (sum, bill) => sum + bill.currentBillValue,
    0
  );
  const totalPaid = approvedRABills.reduce(
    (sum, bill) => sum + bill.paidAmount,
    0
  );
  const pendingPayment = totalValue - totalPaid;

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 220,
      fixed: "left",
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`raBillView-${record?.raBillId}`}
          onClick={() => handleViewRABill(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 230,
      // render: (text, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{text}</div>
      //     <div className="text-muted small mt-1">Section: {record.section}</div>
      //   </div>
      // ),
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 180,
    },
    {
      title: "RA Period",
      key: "raPeriod",
      width: 140,
      render: (_, record) => (
        <div>
          <div className="fw-bold">
            {dayjs(record.raPeriodFrom).format("DD-MMM-YYYY")}
          </div>
          <div className="text-muted small">
            to {dayjs(record.raPeriodTo).format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "RA Bill Value",
      dataIndex: "grossTotal",
      key: "grossTotal",
      width: 150,
      align: "right",
      render: (value) => (
        <div className="fw-bold">{formatCurrency(value, "INR")}</div>
      ),
      sorter: (a, b) => a.grossTotal - b.grossTotal,
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 140,
      align: "right",
      render: (paidAmount, record) => (
        <div>
          <div className="fw-bold text-success">
            {formatCurrency(paidAmount, "INR")}
          </div>
          <div className="text-muted small">
            {getPaymentProgress(paidAmount, record.grossTotal)}% paid
          </div>
        </div>
      ),
    },
    {
      title: "Balance Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 140,
      align: "right",
      render: (balance) => (
        <div
          className="fw-bold"
          style={{ color: balance > 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {formatCurrency(balance, "INR")}
        </div>
      ),
    },
    {
      title: "Payment Progress",
      key: "progress",
      width: 150,
      align: "center",
      render: (_, record) => {
        const progress = getPaymentProgress(
          record.paidAmount,
          record.grossTotal
        );
        const color =
          progress === 100 ? "#52c41a" : progress >= 50 ? "#faad14" : "#ff4d4f";
        return (
          <Progress
            percent={progress}
            size="small"
            strokeColor={color}
            showInfo={true}
          />
        );
      },
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 130,
      render: (status) => (
        <Tag color={getPaymentStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Approved Date",
      dataIndex: "approvedAt",
      key: "approvedAt",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD-MMM-YYYY") : "---"),
      sorter: (a, b) => dayjs(a.approvedAt).unix() - dayjs(b.approvedAt).unix(),
    },
    {
      title: "Last Payment",
      key: "lastContactDate",
      width: 140,
      render: (_, record) => (
        <div>
          {record.lastContactDate ? (
            <>
              <div className="fw-bold">
                {dayjs(record.lastContactDate).format("DD-MMM-YY")}
              </div>
              <div className="text-muted small">{record.paymentMode}</div>
            </>
          ) : (
            <span className="text-muted">No Payment</span>
          )}
        </div>
      ),
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 150,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="primary"
    //         size="small"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewRABill(record)}
    //       >
    //         View Details
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="ra-bills-view-approved"
      title="RA Bills"
      subtitle="Comprehensive view of all approved Running Account Bills with payment tracking"
      columns={columns}
      dataSource={approvedRABills}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <RABillAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1900,
        showSearch: true,
        searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
        kpiWidgets: [
          {
            title: "Total Approved Bills",
            value: totalApproved,
            icon: <CheckCircleOutlined />,
            color: "#52c41a",
          },
          {
            title: "Total Bill Value",
            value: formatCurrency(totalValue, "INR"),
            icon: <DollarOutlined />,
            color: "#1890ff",
          },
          {
            title: "Amount Paid",
            value: formatCurrency(totalPaid, "INR"),
            icon: <CalendarOutlined />,
            color: "#52c41a",
          },
          {
            title: "Pending Payment",
            value: formatCurrency(pendingPayment, "INR"),
            icon: <UserOutlined />,
            color: "#ff4d4f",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Payment Status</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Payment Status"
                    value={filters.paymentStatus}
                    onChange={(value) =>
                      setFilters({ ...filters, paymentStatus: value })
                    }
                  >
                    <Option value="Paid">Paid</Option>
                    <Option value="Partially Paid">Partially Paid</Option>
                    <Option value="Unpaid">Unpaid</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="ABC Infra Ltd">ABC Infra Ltd</Option>
                    <Option value="XYZ Constructions">XYZ Constructions</Option>
                    <Option value="BuildTech Pvt Ltd">BuildTech Pvt Ltd</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">RA Bill #</label>
                  <Input
                    placeholder="Enter RA Bill Number"
                    value={filters.raBillNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, raBillNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, woNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Site</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Site"
                    value={filters.site}
                    onChange={(value) =>
                      setFilters({ ...filters, site: value })
                    }
                  >
                    <Option value="Urban Rise - Tower A">
                      Urban Rise - Tower A
                    </Option>
                    <Option value="Urban Rise - Tower B">
                      Urban Rise - Tower B
                    </Option>
                    <Option value="Urban Rise - Tower C">
                      Urban Rise - Tower C
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Project</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Project"
                    value={filters.project}
                    onChange={(value) =>
                      setFilters({ ...filters, project: value })
                    }
                  >
                    <Option value="Tower Construction Phase 1">
                      Tower Construction Phase 1
                    </Option>
                    <Option value="Tower Construction Phase 2">
                      Tower Construction Phase 2
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Approval Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                    placeholder={["From Date", "To Date"]}
                  />
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default ViewRABillsApproved;
